export function recommendationRequestUrl(endpoint, currentHref, { sectionId, productId, limit, intent }) {
  const current = new URL(currentHref);
  const url = new URL(endpoint, current);
  if (url.origin !== current.origin || !/\/recommendations\/products\/?$/.test(url.pathname))
    throw new Error('Invalid recommendations endpoint');
  if (!sectionId || !/^[1-9]\d*$/.test(String(productId)) || !['related', 'complementary'].includes(intent))
    throw new Error('Invalid recommendations context');
  for (const name of ['country', 'locale', 'preview_theme_id', 'preview_key', '_fd', 'pb']) {
    if (current.searchParams.has(name)) url.searchParams.set(name, current.searchParams.get(name));
  }
  url.searchParams.set('section_id', sectionId);
  url.searchParams.set('product_id', productId);
  url.searchParams.set('limit', String(Math.min(10, Math.max(1, Number.parseInt(limit, 10) || 4))));
  url.searchParams.set('intent', intent);
  url.hash = '';
  return url;
}

export class MeriProductRecommendations extends (globalThis.HTMLElement || class {}) {
  connectedCallback() {
    this.cleanup();
    this.events = new AbortController();
    this.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('[data-recommendations-retry]')) this.load();
      },
      { signal: this.events.signal },
    );
    if (this.dataset.ready === 'true') return;
    if (this.dataset.editor === 'true' || !globalThis.IntersectionObserver) {
      this.load();
    } else {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer?.disconnect();
          this.load();
        },
        { rootMargin: '300px' },
      );
      this.observer.observe(this);
    }
  }
  disconnectedCallback() {
    this.cleanup();
  }
  cleanup() {
    this.epoch = (this.epoch || 0) + 1;
    this.events?.abort();
    this.observer?.disconnect();
    this.request?.abort();
    clearTimeout(this.timer);
    this.request = null;
    this.pending = false;
    this.removeAttribute('aria-busy');
  }
  async load() {
    if (!this.isConnected || this.pending || this.dataset.ready === 'true') return;
    const epoch = this.epoch;
    const request = new AbortController();
    this.request = request;
    this.pending = true;
    this.setAttribute('aria-busy', 'true');
    const error = this.querySelector('[data-recommendations-error]');
    if (error) error.hidden = true;
    const timer = (this.timer = setTimeout(() => request.abort(), 10000));
    const current = () => this.isConnected && epoch === this.epoch;
    try {
      const url = recommendationRequestUrl(this.dataset.url, location.href, this.dataset);
      const response = await fetch(url, { signal: request.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Recommendations unavailable');
      const text = await response.text();
      if (!current()) return;
      if (request.signal.aborted) throw new Error('Recommendations timed out');
      const html = new DOMParser().parseFromString(text, 'text/html');
      const result = [...html.querySelectorAll('meri-product-recommendations')].find(
        (element) => element.dataset.sectionId === this.dataset.sectionId && element.dataset.ready === 'true',
      );
      const content = result?.querySelector('[data-recommendations-content]');
      const destination = this.querySelector('[data-recommendations-content]');
      if (!content || !destination) throw new Error('Incomplete recommendations response');
      const presentation = this.querySelector('[data-recommendations-presentation]');
      const incomingCards = content.querySelector('[data-recommendation-cards]');
      if (presentation && Number(content.dataset.count) > 0) {
        const fragment = presentation.content.cloneNode(true);
        const cardList = fragment.querySelector('[data-recommendation-cards]');
        if (!cardList || !incomingCards) throw new Error('Incomplete recommendation cards');
        // The endpoint returns saved section settings. Keep the editor's current
        // presentation, and use the response only for official product cards.
        cardList.replaceChildren(...incomingCards.childNodes);
        destination.replaceChildren(fragment);
      } else if (presentation) {
        destination.replaceChildren();
      } else {
        destination.replaceChildren(...content.childNodes);
      }
      destination.dataset.count = content.dataset.count || '0';
      const empty = this.querySelector('[data-recommendations-empty]');
      if (empty) empty.hidden = Number(destination.dataset.count) > 0;
      this.dataset.ready = 'true';
    } catch {
      if (current() && error) error.hidden = false;
    } finally {
      clearTimeout(timer);
      if (current()) {
        this.pending = false;
        this.request = null;
        this.timer = null;
        this.removeAttribute('aria-busy');
      }
    }
  }
}

if (globalThis.customElements && !customElements.get('meri-product-recommendations')) {
  customElements.define('meri-product-recommendations', MeriProductRecommendations);
}
